import {inject, Injectable} from '@angular/core';
import {EventBusPort} from '@platform/event-bus/domain/ports/event-bus.port';
import {PaymentStatusChangedEvent} from '@platform/event-bus/domain/events/payment/payment-status-changed.event';

import {PaymentChargeStore} from './payment-charge.store';
import {PaymentQueryService} from './payment-query.service';
import {Payment} from '../../../domain/entities/payment.entity';
import {PaymentError} from '../../../domain/model/payment.errors';

@Injectable({providedIn: 'root'})
export class PaymentCommandService {
  private readonly store = inject(PaymentChargeStore);
  private readonly query = inject(PaymentQueryService);
  private readonly eventBus = inject(EventBusPort);

  markProcessing(payment: Payment): void {
    payment.markAsProcessing();
    this.saveAndNotify(payment);
  }

  markCheckout(payment: Payment, txId: string): void {
    payment.markAsCheckout(txId);
    this.saveAndNotify(payment);
  }

  markSuccess(payment: Payment): void {
    payment.markAsSuccess();
    this.saveAndNotify(payment);
  }

  markFailed(payment: Payment, error: unknown): void {
    payment.markAsFailed(PaymentError.extractKey(error));
    this.saveAndNotify(payment);
  }

  markError(payment: Payment, error: unknown): void {
    payment.markAsFailed(PaymentError.extractKey(error));
    this.saveAndNotify(payment);
  }

  save(payment: Payment): void {
    const dto = payment.toDTO();
    const payments = this.query.getAllDtos().filter((p) => p.order.id !== dto.order.id);

    this.store.set([...payments, dto]);
  }

  private saveAndNotify(payment: Payment): void {
    this.save(payment);
    this.notify(payment);
  }

  private notify(payment: Payment): void {
    this.eventBus.publish(
      new PaymentStatusChangedEvent({
        orderId: payment.order.id,
        status: payment.status,
        amount: payment.amount,
        errorCode: payment.error,
      })
    );
  }

  saveAll(payments: Payment[]): void {
    this.store.set(payments.map((p) => p.toDTO()));
  }

  clear(): void {
    this.store.clear();
  }
}
